import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import VehicleCard from '../components/vehicle/VehicleCard';
import VehicleDetailModal from '../components/vehicle/VehicleDetailModal';
import RangeCalendar from '../components/calendar/RangeCalendar';
import TimePicker from '../components/calendar/TimePicker';
import { useBookingStore } from '../stores/bookingStore';
import { Vehicle } from '../types';
import { apiClient } from '../api/client';
import { formatDateTimeDisplay, formatPrice, calculateRentalPrice } from '../utils/dateUtils';
import Footer from '../components/Footer';
import { getVehicleImage } from '../utils/vehicleImageMap';
import VehicleReviews from '../components/vehicle/VehicleReviews';
import { setSEO } from '../utils/seo';
import { getCompanyEmoji } from '../utils/vehicleUtils';
import {
  localizeCarClass,
  localizeCompany,
  localizeLocation,
  localizeVehicleModel,
} from '../utils/localization';

const VehicleListPage: React.FC = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();

  useEffect(() => {
    window.scrollTo(0, 0);
    setSEO('vehicles');
  }, []);
  const {
    startDateTime,
    endDateTime,
    selectedVehicle,
    selectedCarClasses,
    selectedFuels,
    setSelectedVehicle,
    setStartDateTime,
    setEndDateTime,
    setSelectedCarClasses,
    setSelectedFuels,
    getTotalHours,
    getExactDuration,
  } = useBookingStore();

  const [vehicles, setVehicles] = useState<Vehicle[]>([]);
  const [filteredVehicles, setFilteredVehicles] = useState<Vehicle[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string>('');
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailVehicle, setDetailVehicle] = useState<Vehicle | null>(null);

  // Filter options from API
  const [carClasses, setCarClasses] = useState<string[]>([]);

  // Date/Time editing modal
  const [showDateTimeModal, setShowDateTimeModal] = useState(false);
  const [tempStartDate, setTempStartDate] = useState<Date | null>(null);
  const [tempEndDate, setTempEndDate] = useState<Date | null>(null);
  const [tempStartTime, setTempStartTime] = useState<string>('09:00');
  const [tempEndTime, setTempEndTime] = useState<string>('18:00');

  // Local state for filters (desktop uses this directly)
  const [localSelectedCarClasses, setLocalSelectedCarClasses] = useState<string[]>([]);

  // Mobile menu state
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const fuelOptions = [
    { value: '휘발유', label: t('vehicleList.gasoline') },
    { value: 'LPG', label: t('vehicleList.lpg') },
    { value: '경유', label: t('vehicleList.diesel') },
    { value: '전기', label: t('vehicleList.electric') },
    { value: '하이브리드', label: t('vehicleList.hybrid') },
  ];
  const weekdays = t('vehicleList.weekdays', { returnObjects: true }) as string[];
  const getFuelLabel = (fuelType: string) => fuelOptions.find((fuel) => fuel.value === fuelType)?.label || fuelType;
  const isEnglish = i18n.language.startsWith('en');
  const carClassLabels: Record<string, string> = {
    경차: 'Compact',
    소형: 'Economy',
    준중형: 'Compact Sedan',
    중형: 'Midsize',
    대형: 'Full-size',
    수입: 'Imported',
    RV: 'RV',
    SUV: 'SUV',
  };
  const companyLabels: Record<string, string> = {
    현대: 'Hyundai',
    기아: 'Kia',
    테슬라: 'Tesla',
    제네시스: 'Genesis',
    쉐보레: 'Chevrolet',
    르노: 'Renault',
    쌍용: 'KG Mobility',
    KG모빌리티: 'KG Mobility',
  };
  const locationLabels: Record<string, string> = {
    본사: 'Main Office',
    서면: 'Seomyeon',
    가야: 'Gaya',
  };
  const modelLabels: Record<string, string> = {
    베뉴: 'Venue',
    '제네시스 G80': 'Genesis G80',
    G80: 'Genesis G80',
    '테슬라Model Y': 'Tesla Model Y',
    '테슬라 Model Y': 'Tesla Model Y',
    'Model Y': 'Model Y',
    '볼트 EV': 'Bolt EV',
    '볼트(EV)': 'Bolt EV',
    아반떼: 'Avante',
    '아반떼 AD': 'Avante AD',
    '아반떼 CN7': 'Avante CN7',
    '아반떼 CN7 FL': 'Avante CN7 FL',
    K5: 'K5',
    '3세대 K5': '3rd Gen K5',
    소나타: 'Sonata',
    '소나타 DN8': 'Sonata DN8',
    쏘나타: 'Sonata',
    '쏘나타 디 엣지': 'Sonata The Edge',
    그랜저: 'Grandeur',
    그랜져: 'Grandeur',
    '그랜저 GN7': 'Grandeur GN7',
    '그랜져 GN7': 'Grandeur GN7',
    캐스퍼: 'Casper',
    레이: 'Ray',
    토레스: 'Torres',
    스포티지: 'Sportage',
    카니발: 'Carnival',
    '4세대 카니발': '4th Gen Carnival',
    '4세대카니발': '4th Gen Carnival',
    '카니발 리무진 7인승': 'Carnival Limousine 7-Seater',
    '카니발 리무진 4세대 7인승': '4th Gen Carnival Limousine 7-Seater',
    '4세대 카니발 리무진': '4th Gen Carnival Limousine',
    '4세대카니발 리무진': '4th Gen Carnival Limousine',
    '4세대카니발리무진': '4th Gen Carnival Limousine',
    '카니발 9인승': 'Carnival 9-Seater',
    'All New 카니발 9인승': 'All New Carnival 9-Seater',
    'All New 카니발 4세대 9인승': 'All New 4th Gen Carnival 9-Seater',
    '카니발 리무진': 'Carnival Limousine',
    'All New 카니발 리무진': 'All New Carnival Limousine',
    그랜드스타렉스: 'Grand Starex',
    스타리아: 'Staria',
    '스타리아 9인승': 'Staria 9-Seater',
    '스타리아 11인승': 'Staria 11-Seater',
    스타렉스: 'Starex',
    쏠라티: 'Solati',
    셀토스: 'Seltos',
    쏘렌토: 'Sorento',
    '4세대 쏘렌토 FL': '4th Gen Sorento FL',
    '소렌토MQ4 FL': 'Sorento MQ4 FL',
    싼타페: 'Santa Fe',
    투싼: 'Tucson',
    팰리세이드: 'Palisade',
    '디 올뉴 펠리세이드': 'The All-New Palisade',
  };
  const getCarClassLabel = (carClass?: string) => {
    return localizeCarClass(carClass, i18n.language);
  };
  const getCompanyLabel = (company?: string) => {
    return localizeCompany(company, i18n.language);
  };
  const getLocationLabel = (location?: string) => {
    return localizeLocation(location, i18n.language);
  };
  const getModelLabel = (modelName?: string) => {
    return localizeVehicleModel(modelName, i18n.language);
  };

  const handleOpenDateTimeModal = () => {
    if (!startDateTime || !endDateTime) return;
    setTempStartDate(startDateTime);
    setTempEndDate(endDateTime);
    const startHour = startDateTime.getHours().toString().padStart(2, '0');
    const startMin = startDateTime.getMinutes().toString().padStart(2, '0');
    const endHour = endDateTime.getHours().toString().padStart(2, '0');
    const endMin = endDateTime.getMinutes().toString().padStart(2, '0');
    setTempStartTime(`${startHour}:${startMin}`);
    setTempEndTime(`${endHour}:${endMin}`);
    setShowDateTimeModal(true);
  };

  const handleSaveDateTimeModal = () => {
    if (tempStartDate && tempEndDate) {
      const [startHour, startMin] = tempStartTime.split(':').map(Number);
      const [endHour, endMin] = tempEndTime.split(':').map(Number);

      // Create new Date using year, month, day to avoid timezone issues
      let newStart = new Date(
        tempStartDate.getFullYear(),
        tempStartDate.getMonth(),
        tempStartDate.getDate(),
        startHour,
        startMin,
        0,
        0
      );
      let newEnd = new Date(
        tempEndDate.getFullYear(),
        tempEndDate.getMonth(),
        tempEndDate.getDate(),
        endHour,
        endMin,
        0,
        0
      );

      // Validate minimum 5 hours rental
      const diffInMinutes = (newEnd.getTime() - newStart.getTime()) / (1000 * 60);
      const MIN_RENTAL_MINUTES = 5 * 60;

      if (diffInMinutes < MIN_RENTAL_MINUTES) {
        // Auto-adjust end time to maintain 5-hour minimum
        const adjustedEndTime = new Date(newStart.getTime() + MIN_RENTAL_MINUTES * 60 * 1000);
        const adjustedEndHour = adjustedEndTime.getHours();
        const adjustedEndMin = adjustedEndTime.getMinutes();

        // If adjusted end time exceeds 22:00 on the same day, move to next day at 08:00
        const isSameDay = tempStartDate.toDateString() === tempEndDate.toDateString();
        if (isSameDay && (adjustedEndHour > 22 || (adjustedEndHour === 22 && adjustedEndMin > 0))) {
          const nextDay = new Date(tempEndDate);
          nextDay.setDate(nextDay.getDate() + 1);
          newEnd = new Date(
            nextDay.getFullYear(),
            nextDay.getMonth(),
            nextDay.getDate(),
            8,
            0,
            0,
            0
          );
          alert(t('vehicleList.returnTimeAdjustedNextDay'));
        } else {
          newEnd = adjustedEndTime;
          alert(t('vehicleList.minimumRentalAdjusted'));
        }
      }

      setStartDateTime(newStart);
      setEndDateTime(newEnd);
    }
    setShowDateTimeModal(false);
  };

  const handleRangeSelect = (start: Date | null, end: Date | null) => {
    setTempStartDate(start);
    setTempEndDate(end);
  };

  // Redirect if no dates selected
  useEffect(() => {
    if (!startDateTime || !endDateTime) {
      navigate('/');
    } else {
      console.log('VehicleListPage - Received DateTime:', {
        startDateTime: startDateTime.toString(),
        startHours: startDateTime.getHours(),
        startMinutes: startDateTime.getMinutes(),
        endDateTime: endDateTime.toString(),
        endHours: endDateTime.getHours(),
        endMinutes: endDateTime.getMinutes(),
      });
    }
  }, [startDateTime, endDateTime, navigate]);

  // Fetch car classes
  useEffect(() => {
    let isMounted = true; // Prevent duplicate API calls

    const fetchFilterOptions = async () => {
      if (!isMounted) return;

      try {
        // Fetch car classes
        const classesResponse = await apiClient.get('/vehicles/classes');
        if (classesResponse.data.code === '0' && Array.isArray(classesResponse.data.data)) {
          const classes = classesResponse.data.data.map((item: any) => item.carClassName || item.carClassname || item.name);

          if (!isMounted) return;
          setCarClasses(classes);
        }
      } catch (err) {
        console.error('Failed to fetch filter options:', err);
      }
    };

    fetchFilterOptions();
    return () => {
      isMounted = false;
    };
  }, []);

  // Fetch available vehicles
  useEffect(() => {
    let isMounted = true; // Prevent duplicate API calls

    const fetchVehicles = async () => {
      if (!startDateTime || !endDateTime || !isMounted) return;

      setLoading(true);
      setError('');

      try {
        const startDate = new Date(startDateTime);
        const endDate = new Date(endDateTime);

        // Format: YYYY-MM-DD HH
        const formatDateTime = (date: Date) => {
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hour = String(date.getHours()).padStart(2, '0');
          return `${year}-${month}-${day} ${hour}`;
        };

        const requestData: any = {
          start_date: formatDateTime(startDate),
          end_date: formatDateTime(endDate),
        };

        // Add filters if selected
        if (selectedCarClasses.length > 0) {
          requestData.car_class = selectedCarClasses.join(',');
        }

        // Add fuel filter
        if (selectedFuels.length > 0) {
          requestData.fuel = selectedFuels.join(',');
        }

        if (!isMounted) return;

        const response = await apiClient.post('/vehicles/search', requestData);

        if (response.data.code === '0' && Array.isArray(response.data.data)) {
          // Group vehicles by model name
          const vehicleGroups = response.data.data.reduce((acc: any, item: any) => {
            const modelName = item.carname || item.model_name;
            if (!acc[modelName]) {
              acc[modelName] = {
                model_name: modelName,
                car_class: item.carClassname || item.car_class || '-',
                fuel_type: item.fuel || item.fuel_type || '-',
                seating_capacity: parseInt(item.people) || 5,
                weekday_rate: parseInt(item.daymoney) || 50000, // 주간(평일) 요금
                weekend_rate: parseInt(item.weekmoney) || 70000, // 주말/휴일 요금
                five_hour_rate: parseInt(item.fivemoney) || 0, // 5시간 요금
                image_url: getVehicleImage(modelName),
                company: item.company || '', // 제조사 정보
                license_plate: item.carnum || item.license_plate || '', // 차량번호
                CarSomoName: item.CarSomoName || item.carsomoname || '', // 옵션 정보
                features: [
                  item.special ? `특징: ${item.special}` : '',
                  item.locationName ? `위치: ${item.locationName}` : '',
                ].filter(Boolean),
                available: true,
                count: 0,
                ids: [],
                idx: item.idx, // Store first idx for image lookup
              };
            }
            acc[modelName].count += 1;
            acc[modelName].ids.push(item.idx);
            // Update license_plate if not set yet
            if (!acc[modelName].license_plate && (item.carnum || item.license_plate)) {
              acc[modelName].license_plate = item.carnum || item.license_plate;
            }
            return acc;
          }, {});

          // Convert grouped object to array and add sequential IDs
          const vehicleData = Object.values(vehicleGroups).map((vehicle: any, index) => ({
            ...vehicle,
            id: vehicle.idx || index + 1, // Use idx as id for image lookup
          }));

          if (!isMounted) return;

          setVehicles(vehicleData);
          setFilteredVehicles(vehicleData);
        } else {
          const errorMsg = response.data.msg || t('vehicleList.loadFailed');
          console.error('API returned non-success code:', response.data);

          if (!isMounted) return;
          setError(errorMsg);
        }
      } catch (err: any) {
        console.error('Failed to fetch vehicles:', err);
        console.error('Error details:', err.response?.data);

        if (!isMounted) return;
        setError(t('vehicleList.loadFailedWithMessage', { message: err.message }));
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchVehicles();
    return () => {
      isMounted = false;
    };
  }, [startDateTime, endDateTime, selectedCarClasses, selectedFuels, t]);

  if (!startDateTime || !endDateTime) {
    return null;
  }

  const totalHours = getTotalHours();
  const exactDuration = getExactDuration();

  // Format duration display
  const formatDuration = () => {
    const days = Math.floor(exactDuration.hours / 24);
    const hours = exactDuration.hours % 24;
    const minutes = exactDuration.minutes;

    let result = '';
    if (days > 0) result += `${days}${t('vehicleList.day')} `;
    if (hours > 0) result += `${hours}${t('vehicleList.hour')}`;
    if (minutes > 0) result += ` ${minutes}${t('vehicleList.minute')}`;

    return result.trim();
  };

  // Show loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
          <p className="text-gray-600">{t('vehicleList.loading')}</p>
        </div>
      </div>
    );
  }

  const handleSelectVehicle = (vehicle: Vehicle) => {
    setSelectedVehicle(vehicle);
    navigate('/vehicle-detail', { state: { vehicle } });
  };

  const handleViewDetails = (vehicle: Vehicle) => {
    navigate('/vehicle-detail', { state: { vehicle } });
  };

  const handleContinue = () => {
    if (!selectedVehicle) {
      alert(t('vehicleList.selectVehicle'));
      return;
    }
    navigate('/checkout');
  };

  const handleBackToHome = () => {
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Date Time Modal - at page level */}
      {showDateTimeModal && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black bg-opacity-30 z-[60]"
            onClick={() => setShowDateTimeModal(false)}
          />

          {/* Modal positioned at center */}
          <div className="fixed left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-80 max-w-[90vw] z-[70]">
            <div className="bg-white rounded-lg shadow-2xl">
              {/* Header */}
              <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-3 py-2 rounded-t-lg flex items-center justify-between">
                <h3 className="font-bold text-sm">{t('vehicleList.dateTimeSelect')}</h3>
                <button
                  onClick={() => setShowDateTimeModal(false)}
                  className="p-0.5 hover:bg-white hover:bg-opacity-20 rounded transition-colors"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Content */}
              <div className="p-2">
                {/* Calendar */}
                <div className="mb-2">
                  <RangeCalendar
                    startDate={tempStartDate}
                    endDate={tempEndDate}
                    onSelectRange={handleRangeSelect}
                  />
                </div>

                {/* Time Selection */}
                <div className="grid grid-cols-2 gap-2 mb-2">
                  {/* Start Time */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('vehicleList.rentalStart')}</label>
                    <select
                      value={tempStartTime}
                      onChange={(e) => {
                        const newStartTime = e.target.value;
                        setTempStartTime(newStartTime);

                        // Auto-adjust end time if less than 5 hours
                        if (tempStartDate && tempEndDate) {
                          const [startHour, startMin] = newStartTime.split(':').map(Number);
                          const [endHour, endMin] = tempEndTime.split(':').map(Number);

                          const isSameDay = tempStartDate.toDateString() === tempEndDate.toDateString();

                          if (isSameDay) {
                            const startMinutes = startHour * 60 + startMin;
                            const endMinutes = endHour * 60 + endMin;
                            const diff = endMinutes - startMinutes;

                            // If less than 5 hours, adjust end time
                            if (diff < 5 * 60) {
                              const newEndMinutes = startMinutes + 5 * 60;
                              const newEndHour = Math.floor(newEndMinutes / 60);
                              const newEndMin = newEndMinutes % 60;

                              // If end time exceeds 22:00, move to next day
                              if (newEndHour > 22 || (newEndHour === 22 && newEndMin > 0)) {
                                const nextDay = new Date(tempEndDate);
                                nextDay.setDate(nextDay.getDate() + 1);
                                setTempEndDate(nextDay);
                                setTempEndTime('08:00');
                              } else {
                                setTempEndTime(`${newEndHour.toString().padStart(2, '0')}:${newEndMin.toString().padStart(2, '0')}`);
                              }
                            }
                          }
                        }
                      }}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Array.from({ length: 29 }, (_, i) => {
                        const totalMinutes = 8 * 60 + i * 30;
                        const hour = Math.floor(totalMinutes / 60);
                        const minute = totalMinutes % 60;
                        if (hour > 22) return null;

                        // Check if this time is in the past for today
                        const now = new Date();
                        const isToday = tempStartDate && tempStartDate.toDateString() === now.toDateString();
                        let disabled = false;
                        if (isToday) {
                          const currentHour = now.getHours();
                          const currentMinute = now.getMinutes();
                          const currentTotalMinutes = currentHour * 60 + currentMinute;
                          disabled = totalMinutes < currentTotalMinutes;
                        }

                        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                        return (
                          <option key={timeString} value={timeString} disabled={disabled}>
                            {timeString}
                          </option>
                        );
                      }).filter(Boolean)}
                    </select>
                  </div>

                  {/* End Time */}
                  <div>
                    <label className="block text-xs font-medium text-gray-700 mb-0.5">{t('vehicleList.returnTime')}</label>
                    <select
                      value={tempEndTime}
                      onChange={(e) => setTempEndTime(e.target.value)}
                      className="w-full px-2 py-1 border border-gray-300 rounded text-xs focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {Array.from({ length: 29 }, (_, i) => {
                        const totalMinutes = 8 * 60 + i * 30; // Start from 8:00, increment by 30 minutes
                        const hour = Math.floor(totalMinutes / 60);
                        const minute = totalMinutes % 60;
                        if (hour > 22) return null; // Stop at 22:00

                        // Calculate minimum end time (start time + 5 hours)
                        const [startHour, startMin] = tempStartTime.split(':').map(Number);
                        const startTotalMinutes = startHour * 60 + startMin;

                        // Check if same day or different day
                        const isSameDay = tempStartDate && tempEndDate &&
                          tempStartDate.toDateString() === tempEndDate.toDateString();

                        let disabled = false;
                        if (isSameDay) {
                          // Minimum 5 hours from start time
                          const minEndMinutes = startTotalMinutes + 5 * 60;
                          disabled = totalMinutes < minEndMinutes;
                        }

                        const timeString = `${hour.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')}`;
                        return (
                          <option key={timeString} value={timeString} disabled={disabled}>
                            {timeString}
                          </option>
                        );
                      }).filter(Boolean)}
                    </select>
                  </div>
                </div>

                {/* Buttons */}
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowDateTimeModal(false)}
                    className="flex-1 px-3 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded transition-colors text-xs font-medium"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    onClick={handleSaveDateTimeModal}
                    disabled={!tempStartDate || !tempEndDate}
                    className="flex-1 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors disabled:bg-gray-300 disabled:cursor-not-allowed text-xs font-medium"
                  >
                    {t('common.apply')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-cyan-500 shadow-md">
        <div className="max-w-7xl mx-auto px-4 py-3">
          {/* Desktop Header */}
          <div className="hidden md:flex items-center justify-between relative">
            <button
              onClick={handleBackToHome}
              className="flex items-center gap-2 text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              <div className="flex flex-col items-start">
                <span className="text-sm text-white/90 font-semibold">{t('vehicleList.home')}</span>
              </div>
            </button>
            <div className="absolute left-1/2 transform -translate-x-1/2 flex items-center gap-3">
              <img src="/logo.svg" alt={t('brand.name')} className="h-8" />
              <h1 className="text-xl font-semibold text-white">{t('vehicleList.title')}</h1>
            </div>
            <div className="w-20"></div>
          </div>

          {/* Mobile Header */}
          <div className="md:hidden flex items-center gap-3">
            <button
              onClick={handleBackToHome}
              className="flex items-center text-white/90 hover:text-white transition-colors"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div className="flex items-center gap-2 flex-1">
              <img src="/logo.svg" alt={t('brand.name')} className="h-7" />
              <h1 className="text-lg font-semibold text-white truncate">{t('vehicleList.title')}</h1>
            </div>
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 text-white hover:bg-white/10 rounded-lg transition-colors"
              aria-label={t('vehicleList.menu')}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 bg-black/50 z-40 md:hidden"
            onClick={() => setIsMobileMenuOpen(false)}
          />

          {/* Slide-in Menu */}
          <div className={`fixed top-0 right-0 h-full w-64 bg-white z-50 md:hidden shadow-2xl transform transition-transform duration-300 ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
            <div className="flex flex-col h-full">
              {/* Menu Header */}
              <div className="bg-gradient-to-r from-blue-600 to-cyan-500 p-4 flex items-center justify-between">
                <h2 className="text-white font-bold text-lg">{t('vehicleList.menu')}</h2>
                <button
                  onClick={() => setIsMobileMenuOpen(false)}
                  className="text-white p-1 hover:bg-white/10 rounded transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              {/* Menu Items */}
              <nav className="flex-1 p-4">
                <ul className="space-y-2">
                  <li>
                    <a
                      href="/"
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
                      </svg>
                      <span className="font-medium text-gray-700">{t('vehicleList.home')}</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/vehicles"
                      className="flex items-center gap-3 p-3 rounded-lg bg-blue-50 text-blue-600"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" />
                      </svg>
                      <span className="font-medium">{t('vehicleList.vehicleSelect')}</span>
                    </a>
                  </li>
                  <li>
                    <a
                      href="/rental-guide"
                      className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-100 transition-colors"
                    >
                      <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                      </svg>
                      <span className="font-medium text-gray-700">{t('vehicleList.serviceGuide')}</span>
                    </a>
                  </li>
                </ul>
              </nav>

              {/* Contact Info */}
              <div className="p-4 border-t border-gray-200 bg-gray-50">
                <div className="text-sm text-gray-600 mb-2">{t('vehicleList.customerCenter')}</div>
                <a href="tel:1600-4412" className="text-xl font-bold text-blue-600 hover:text-blue-700">
                  1600-4412
                </a>
                <div className="text-xs text-gray-500 mt-1">08:00 - 22:00</div>
              </div>
            </div>
          </div>
        </>
      )}

      {/* Main Content - Split Layout */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Left Sidebar - Booking Info (Desktop Only) */}
          <div className="hidden md:block w-80 flex-shrink-0">
            <div className="sticky top-6 space-y-4">
              {/* Booking Summary Card */}
              <div className="bg-white rounded-lg shadow-md p-6 relative">
                {/* Date Card - Clickable */}
                <div
                  onClick={handleOpenDateTimeModal}
                  className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 mb-4 cursor-pointer hover:shadow-md transition-all border-2 border-gray-200 hover:border-gray-300"
                >
                  <div className="space-y-3">
                    {/* Start Date */}
                    <div>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-base text-gray-600 font-semibold">{t('vehicleList.rentalDate')}:</span>
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-2xl font-bold text-gray-900">
                            {startDateTime.getMonth() + 1}/{startDateTime.getDate()}
                          </span>
                          <span className="text-base font-medium text-gray-700">
                            ({weekdays[startDateTime.getDay()]})
                          </span>
                          <span className="text-xl font-bold text-gray-900 ml-2">
                            {startDateTime.getHours().toString().padStart(2, '0')}:{startDateTime.getMinutes().toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* End Date */}
                    <div>
                      <div className="flex items-center gap-2">
                        <span className="text-base text-gray-600 font-semibold">{t('vehicleList.returnDate')}:</span>
                        <div className="flex items-baseline gap-0.5">
                          <span className="text-2xl font-bold text-gray-900">
                            {endDateTime.getMonth() + 1}/{endDateTime.getDate()}
                          </span>
                          <span className="text-base font-medium text-gray-700">
                            ({weekdays[endDateTime.getDay()]})
                          </span>
                          <span className="text-xl font-bold text-gray-900 ml-2">
                            {endDateTime.getHours().toString().padStart(2, '0')}:{endDateTime.getMinutes().toString().padStart(2, '0')}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Duration */}
                    <div className="flex items-center justify-center gap-2 py-1">
                      <span className="text-sm text-gray-600 font-medium">{t('vehicleList.totalDuration')} :</span>
                      <div className="bg-blue-600 text-white px-4 py-1.5 rounded-full">
                        <span className="text-sm font-bold whitespace-nowrap">
                          {formatDuration()}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Edit hint */}
                  <div className="mt-2 pt-2 border-t border-gray-200 flex items-center justify-center text-xs text-gray-600">
                    <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    {t('vehicleList.changeDate')}
                  </div>
                </div>
              </div>

              {/* Filters Card */}
              <div className="bg-white rounded-lg shadow-md p-4">
                <h2 className="text-base font-bold mb-3 pb-2 border-b">{t('vehicleList.conditions')}</h2>

                <div className="space-y-4">
                  {/* Car Class Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicleList.vehicleGrade')}</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedCarClasses([])}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                          selectedCarClasses.length === 0
                            ? 'border-blue-600 text-blue-600 bg-white'
                            : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                        }`}
                      >
                        {t('vehicleList.all')}
                      </button>
                      {carClasses.map((carClass) => {
                        const isSelected = selectedCarClasses.includes(carClass);
                        return (
                          <button
                            key={carClass}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedCarClasses(selectedCarClasses.filter(c => c !== carClass));
                              } else {
                                setSelectedCarClasses([...selectedCarClasses, carClass]);
                              }
                            }}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                              isSelected
                                ? 'border-blue-600 text-blue-600 bg-white'
                                : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                            }`}
                          >
                            {getCarClassLabel(carClass)}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Fuel Type Filter */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">{t('vehicleList.fuelType')}</label>
                    <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => setSelectedFuels([])}
                        className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                          selectedFuels.length === 0
                            ? 'border-blue-600 text-blue-600 bg-white'
                            : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                        }`}
                      >
                        {t('vehicleList.all')}
                      </button>
                      {fuelOptions.map((fuel) => {
                        const isSelected = selectedFuels.includes(fuel.value);
                        return (
                          <button
                            key={fuel.value}
                            onClick={() => {
                              if (isSelected) {
                                setSelectedFuels(selectedFuels.filter(f => f !== fuel.value));
                              } else {
                                setSelectedFuels([...selectedFuels, fuel.value]);
                              }
                            }}
                            className={`px-3 py-2 rounded-lg text-xs font-medium transition-all border-2 ${
                              isSelected
                                ? 'border-blue-600 text-blue-600 bg-white'
                                : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                            }`}
                          >
                            {fuel.label}
                          </button>
                        );
                      })}
                    </div>
                  </div>

                  {/* Reset Filters Button */}
                  {(selectedCarClasses.length > 0 || selectedFuels.length > 0) && (
                    <button
                      onClick={() => {
                        setSelectedCarClasses([]);
                        setSelectedFuels([]);
                      }}
                      className="w-full px-4 py-2 text-sm text-gray-600 hover:text-gray-900 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                      {t('vehicleList.resetFilters')}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Content - Vehicle List */}
          <div className="flex-1">

          {/* Mobile: Booking Info & Filters - Shown above vehicle list */}
          <div className="md:hidden space-y-4 mb-6">
            {/* Booking Summary Card */}
            <div className="bg-white rounded-lg shadow-md p-4">
              {/* Date Card - Clickable */}
              <div
                onClick={handleOpenDateTimeModal}
                className="bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl p-4 cursor-pointer hover:shadow-md transition-all border-2 border-gray-200 hover:border-gray-300"
              >
                <div className="space-y-2">
                  {/* Start Date */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-semibold">{t('vehicleList.rentalDate')}:</span>
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-lg font-bold text-gray-900">
                          {startDateTime.getMonth() + 1}/{startDateTime.getDate()}
                        </span>
                        <span className="text-xs font-medium text-gray-700">
                          ({weekdays[startDateTime.getDay()]})
                        </span>
                        <span className="text-base font-bold text-gray-900 ml-2">
                          {startDateTime.getHours().toString().padStart(2, '0')}:{startDateTime.getMinutes().toString().padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* End Date */}
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-gray-600 font-semibold">{t('vehicleList.returnDate')}:</span>
                      <div className="flex items-baseline gap-0.5">
                        <span className="text-lg font-bold text-gray-900">
                          {endDateTime.getMonth() + 1}/{endDateTime.getDate()}
                        </span>
                        <span className="text-xs font-medium text-gray-700">
                          ({weekdays[endDateTime.getDay()]})
                        </span>
                        <span className="text-base font-bold text-gray-900 ml-2">
                          {endDateTime.getHours().toString().padStart(2, '0')}:{endDateTime.getMinutes().toString().padStart(2, '0')}
                        </span>
                      </div>
                    </div>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center gap-2 pt-2 border-t border-gray-200">
                    <span className="text-sm text-gray-600 font-semibold">{t('vehicleList.duration')}:</span>
                    <span className="text-base font-bold text-blue-600">{formatDuration()}</span>
                  </div>

                  {/* Edit Hint */}
                  <div className="text-xs text-gray-500 text-center pt-1">
                    {t('vehicleList.changeDateTime')}
                  </div>
                </div>
              </div>
            </div>

            {/* Filters */}
            <div className="bg-white rounded-lg shadow-md p-4">
              {/* Car Class Dropdown for Mobile */}
              <div className="mb-4">
                <label htmlFor="mobile-car-class" className="block text-base font-bold mb-3">
                  {t('vehicleList.carType')}
                </label>
                <select
                  id="mobile-car-class"
                  value={selectedCarClasses.length > 0 ? selectedCarClasses[0] : ''}
                  onChange={(e) => {
                    if (e.target.value === '') {
                      setSelectedCarClasses([]);
                    } else {
                      setSelectedCarClasses([e.target.value]);
                    }
                  }}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg text-base focus:ring-2 focus:ring-blue-500 focus:border-blue-500 bg-white"
                >
                  <option value="">{t('vehicleList.all')}</option>
                  {carClasses.map((carClass) => (
                    <option key={carClass} value={carClass}>
                      {getCarClassLabel(carClass)}
                    </option>
                  ))}
                </select>
              </div>

              {/* Fuel Type Filter for Mobile */}
              <div>
                <label className="block text-base font-bold mb-3">{t('vehicleList.fuelType')}</label>
                <div className="flex flex-wrap gap-2">
                  <button
                    onClick={() => setSelectedFuels([])}
                    className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                      selectedFuels.length === 0
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                    }`}
                  >
                    {t('vehicleList.all')}
                  </button>
                  {fuelOptions.map((fuel) => {
                    const isSelected = selectedFuels.includes(fuel.value);
                    return (
                      <button
                        key={fuel.value}
                        onClick={() => {
                          if (isSelected) {
                            setSelectedFuels(selectedFuels.filter(f => f !== fuel.value));
                          } else {
                            setSelectedFuels([...selectedFuels, fuel.value]);
                          }
                        }}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all border-2 ${
                          isSelected
                            ? 'border-blue-600 text-blue-600 bg-white'
                            : 'border-gray-300 text-gray-700 bg-white hover:border-gray-400'
                        }`}
                      >
                        {fuel.label}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          </div>

          {/* Info */}
          <div className="mb-6 flex justify-between items-center">
            <h2 className="text-2xl font-bold">
              {t('vehicleList.availableCount', { count: filteredVehicles.length })}
            </h2>
            <span className="text-lg font-normal text-gray-600">{t('vehicleList.clickToBook')}</span>
          </div>

          {/* Error Message */}
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-8">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-500 mr-2" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <span className="text-red-800 font-medium">{error}</span>
              </div>
            </div>
          )}

          {/* Vehicles List */}
          <div className="space-y-4">
            {filteredVehicles.map((vehicle) => (
              <div
                key={vehicle.id}
                onClick={() => handleSelectVehicle(vehicle)}
                className="bg-white rounded-lg shadow-sm border-2 border-transparent hover:border-blue-500 hover:shadow-lg transition-all cursor-pointer overflow-hidden"
              >
                {/* Desktop: Horizontal Layout */}
                <div className="hidden md:flex">
                  {/* Vehicle Image */}
                  <div className="w-96 h-64 flex-shrink-0 bg-white relative">
                    <img
                      src={vehicle.image_url || '/placeholder-car.svg'}
                      alt={getModelLabel(vehicle.model_name)}
                      className="w-full h-full object-contain p-8"
                    />
                    {(vehicle as any).count > 0 && (
                      <div className="absolute bottom-3 left-3 bg-blue-400 text-white px-3 py-1.5 rounded-full shadow-md">
                        <div className="flex items-center gap-1">
                          <span className="text-lg font-bold">{(vehicle as any).count}</span>
                          <span className="text-xs font-semibold">{t('vehicleList.availableUnits')}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Vehicle Info */}
                  <div className="flex-1 p-6 flex flex-col justify-between">
                    <div>
                      <div className="flex justify-between items-start mb-3">
                        <div>
                          <h3 className="text-xl font-bold text-gray-900">{getModelLabel(vehicle.model_name)}</h3>
                          <p className="text-base text-gray-500 mt-1">{getCarClassLabel(vehicle.car_class)}</p>
                        </div>
                        <div className="text-right">
                          <div className="inline-block bg-blue-600 text-white px-3 py-1 rounded-md text-xs font-medium mb-1">
                            {t('vehicleList.lowestPrice')}
                          </div>
                          <div className="text-2xl font-bold text-blue-600 mt-1">
                            {(() => {
                              if (!startDateTime || !endDateTime) return '0';

                              const weekdayRate = (vehicle as any).weekday_rate || 50000;
                              const weekendRate = (vehicle as any).weekend_rate || 70000;
                              const fiveHourRate = (vehicle as any).five_hour_rate || 0;

                              const totalAmount = calculateRentalPrice(
                                startDateTime,
                                endDateTime,
                                weekdayRate,
                                weekendRate,
                                fiveHourRate
                              );

                              return totalAmount.toLocaleString();
                            })()}{t('common.won')}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-3">
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                          </svg>
                          <div className="text-sm font-semibold text-gray-900">{vehicle.seating_capacity}{t('vehicleList.seats')}</div>
                        </div>
                        <div className="flex items-start">
                          <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                          </svg>
                          <div className="text-sm font-semibold text-gray-900">{getFuelLabel(vehicle.fuel_type)}</div>
                        </div>
                        {(vehicle as any).company && (
                          <div className="flex items-start">
                            <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                            </svg>
                            <div className="text-sm font-semibold text-gray-900">{getCompanyLabel((vehicle as any).company)}</div>
                          </div>
                        )}
                        {vehicle.features && vehicle.features.length > 0 && (
                          vehicle.features.slice(0, 2).map((feature, idx) => {
                            const parts = feature.split(':').map(p => p.trim());
                            const label = parts.length > 1 ? parts[0] : t('vehicleList.feature');
                            const rawValue = parts.length > 1 ? parts.slice(1).join(':').trim() : feature;
                            const isLocation = label === t('vehicleList.location') || label === '위치';
                            const value = isLocation ? getLocationLabel(rawValue) : rawValue;

                            return (
                              <div key={idx} className="flex items-start">
                                {isLocation ? (
                                  <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                  </svg>
                                ) : (
                                  <svg className="w-5 h-5 text-blue-600 mr-2 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                  </svg>
                                )}
                                <div className="text-sm font-semibold text-gray-900">{value}</div>
                              </div>
                            );
                          })
                        )}
                        <div className="flex items-center">
                          <span className="text-green-600 mr-1">✓</span>
                          <div className="text-sm font-semibold text-gray-900">{t('vehicleList.noExtraFees')}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Mobile: Vertical Layout */}
                <div className="md:hidden">
                  {/* Vehicle Image - Full width on mobile */}
                  <div className="relative h-48 bg-gray-50">
                    <img
                      src={vehicle.image_url || '/placeholder-car.svg'}
                      alt={getModelLabel(vehicle.model_name)}
                      className="w-full h-full object-contain p-4"
                    />
                    {(vehicle as any).count > 0 && (
                      <div className="absolute bottom-3 left-3 bg-blue-400 text-white px-2.5 py-1 rounded-full shadow-md">
                        <div className="flex items-center gap-1">
                          <span className="text-base font-bold">{(vehicle as any).count}</span>
                          <span className="text-xs font-semibold">{t('vehicleList.availableUnits')}</span>
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Vehicle Info - Below image on mobile */}
                  <div className="p-4">
                    {/* Header with price */}
                    <div className="flex justify-between items-start mb-3">
                      <div className="flex-1">
                        <h3 className="text-lg font-bold text-gray-900">{getModelLabel(vehicle.model_name)}</h3>
                        <p className="text-sm text-gray-500 mt-1">{getCarClassLabel(vehicle.car_class)}</p>
                      </div>
                      <div className="text-right ml-3">
                        <div className="inline-block bg-blue-600 text-white px-2 py-0.5 rounded text-xs font-medium mb-1">
                          {t('vehicleList.lowestPrice')}
                        </div>
                        <div className="text-xl font-bold text-blue-600">
                          {(() => {
                            if (!startDateTime || !endDateTime) return '0';

                            const weekdayRate = (vehicle as any).weekday_rate || 50000;
                            const weekendRate = (vehicle as any).weekend_rate || 70000;
                            const fiveHourRate = (vehicle as any).five_hour_rate || 0;

                            const totalAmount = calculateRentalPrice(
                              startDateTime,
                              endDateTime,
                              weekdayRate,
                              weekendRate,
                              fiveHourRate
                            );

                            return totalAmount.toLocaleString();
                          })()}{t('common.won')}
                        </div>
                      </div>
                    </div>

                    {/* Vehicle Details Grid */}
                    <div className="grid grid-cols-2 gap-3 mt-4">
                      <div className="flex items-center text-sm text-gray-700">
                        <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                        </svg>
                        <span className="font-semibold">{vehicle.seating_capacity}{t('vehicleList.seats')}</span>
                      </div>
                      <div className="flex items-center text-sm text-gray-700">
                        <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
                        </svg>
                        <span className="font-semibold">{getFuelLabel(vehicle.fuel_type)}</span>
                      </div>
                      {(vehicle as any).company && (
                        <div className="flex items-center text-sm text-gray-700">
                          <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                          </svg>
                          <span className="font-semibold">{getCompanyLabel((vehicle as any).company)}</span>
                        </div>
                      )}
                      {vehicle.features && vehicle.features.length > 0 && (
                        vehicle.features.slice(0, 1).map((feature, idx) => {
                          const parts = feature.split(':').map(p => p.trim());
                          const label = parts.length > 1 ? parts[0] : t('vehicleList.feature');
                          const rawValue = parts.length > 1 ? parts.slice(1).join(':').trim() : feature;
                          const isLocation = label === t('vehicleList.location') || label === '위치';
                          const value = isLocation ? getLocationLabel(rawValue) : rawValue;

                          return (
                            <div key={idx} className="flex items-center text-sm text-gray-700">
                              {isLocation ? (
                                <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                </svg>
                              ) : (
                                <svg className="w-4 h-4 text-blue-600 mr-2 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                </svg>
                              )}
                              <span className="font-semibold truncate">{value}</span>
                            </div>
                          );
                        })
                      )}
                      <div className="flex items-center text-sm text-gray-700">
                        <span className="text-green-600 mr-1">✓</span>
                        <span className="font-semibold">{t('vehicleList.noExtraFees')}</span>
                      </div>
                    </div>

                    {/* View Details Button */}
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDetails(vehicle);
                      }}
                      className="mt-4 w-full px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      {t('vehicleList.details')}
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Empty State */}
          {filteredVehicles.length === 0 && !error && (
            <div className="text-center py-16">
              <svg className="w-24 h-24 mx-auto text-gray-400 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <h3 className="text-xl font-semibold text-gray-700 mb-2">
                {t('vehicleList.emptyTitle')}
              </h3>
              <p className="text-gray-500 mb-6">
                {t('vehicleList.emptyDesc')}
              </p>
              <button
                onClick={handleBackToHome}
                className="px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors"
              >
                {t('vehicleList.selectDateAgain')}
              </button>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Vehicle Detail Modal */}
      <VehicleDetailModal
        vehicle={detailVehicle}
        isOpen={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        onSelect={() => detailVehicle && handleSelectVehicle(detailVehicle)}
      />

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default VehicleListPage;
